import axios from 'axios';
import { logError } from '../utils/errorLogger';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getApiBaseUrl } from '../utils/api';

const API_BASE_URL = getApiBaseUrl();

axios.defaults.baseURL = API_BASE_URL;
// SECURITY: Enable httpOnly cookie handling for refresh token
axios.defaults.withCredentials = true;

/**
 * SECURITY: Store CSRF token in document meta tag
 */
const setCSRFToken = (token) => {
  if (!token) return;
  let meta = document.querySelector('meta[name="csrf-token"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'csrf-token';
    document.head.appendChild(meta);
  }
  meta.content = token;
};

/**
 * SECURITY: Get CSRF token from meta tag
 */
const getCSRFToken = () => {
  return document.querySelector('meta[name="csrf-token"]')?.content;
};

// Add request interceptor for CSRF token
axios.interceptors.request.use((config) => {
  const csrfToken = getCSRFToken();
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});

// Create the AuthContext
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  // IMPORTANT: logout must be defined BEFORE verifyToken to avoid TDZ in minified builds
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const verifyToken = useCallback(async () => {
    try {
      // Real token verification via API
      if (token) {
        const response = await axios.get('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success && response.data.user) {
          setUser(response.data.user);
        } else {
          throw new Error('Invalid token response');
        }
      }
    } catch (error) {
      logError(error, { context: 'AuthProvider.verifyToken' });
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Verify token and get user info
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token, verifyToken]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password,
      });

      // Allow admin and super_admin roles
      const allowedRoles = ['admin', 'super_admin'];
      const userRole = response.data.user?.role;

      if (response.data.success && allowedRoles.includes(userRole)) {
        // Worker API returns accessToken (new format), fallback to token (old format)
        const newToken = response.data.accessToken || response.data.token;
        const userData = response.data.user;
        // SECURITY: Store CSRF token from response
        const csrfToken = response.data.csrfToken;

        if (!newToken) {
          throw new Error('No authentication token received');
        }

        setToken(newToken);
        setUser(userData);
        localStorage.setItem('admin_token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        // SECURITY: Store CSRF token in meta tag for protection
        if (csrfToken) {
          setCSRFToken(csrfToken);
        }

        return { success: true };
      } else if (response.data.success && !allowedRoles.includes(userRole)) {
        throw new Error('Admin access required');
      } else {
        throw new Error('Invalid login response');
      }
    } catch (error) {
      logError(error, { context: 'AuthProvider.login' });
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Login failed',
      };
    }
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      const response = await axios.patch('/api/auth/profile', profileData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        return { success: true };
      } else {
        throw new Error('Invalid update response');
      }
    } catch (error) {
      logError(error, { context: 'AuthProvider.updateProfile' });
      // Fallback: update local state even if API fails
      setUser((prev) => ({ ...prev, ...profileData }));
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Update failed',
      };
    }
  }, [token]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    // Treat token presence as authenticated; role checks happen server-side and/or after /me.
    isAuthenticated: !!token,
    login,
    logout,
    updateProfile,
  }), [user, token, loading, login, logout, updateProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
